// Regras do SSR das abas do ensaio.
//
// Sem imports: roda no Nuxt e no node:test.

// O SSR espera a API no máximo isso; o useAsyncData não tem opção de timeout.
export const TIMEOUT_SSR_MS = 4000;
// A busca do cliente traz a resposta completa (miniaturas base64, URL de
// reprodução) e pode estar numa conexão móvel lenta.
export const TIMEOUT_CLIENTE_MS = 15000;

type ErroHttp = {
  statusCode?: unknown;
  response?: { status?: unknown };
  cause?: unknown;
} | null | undefined;

function statusDaResposta(erro: ErroHttp): number | undefined {
  const status = erro?.response?.status;
  return typeof status === "number" ? status : undefined;
}

/**
 * Status da página quando a busca dos metadados falha: 404 só quando a API
 * respondeu 404 (slug inexistente ou ensaio não publicado). Timeout, 5xx e rede
 * viram 503, para uma queda da API não tirar páginas do índice.
 *
 * Aceita o erro do axios, o erro do Nuxt e o erro do Nuxt que embrulha o do
 * axios (`cause`), porque o handler de outra aba pode ter feito a busca. A
 * resposta HTTP vem antes do `statusCode`, que no embrulho é o 500 padrão.
 */
export function statusDoErroDaApi(erro: unknown): 404 | 503 {
  const e = erro as ErroHttp;
  const status =
    statusDaResposta(e) ??
    statusDaResposta(e?.cause as ErroHttp) ??
    (typeof e?.statusCode === "number" ? e.statusCode : undefined);
  return status === 404 ? 404 : 503;
}

function ehObjeto(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === "object" && !Array.isArray(v);
}

function respostaComShoot(raw: unknown): raw is { shoot: unknown[] } {
  return (
    ehObjeto(raw) &&
    !("message" in raw) &&
    Array.isArray(raw.shoot) &&
    raw.shoot.length > 0 &&
    raw.shoot.every(ehObjeto)
  );
}

/**
 * A resposta completa de `v2/modelos/{url}/photos` serve para substituir o
 * SSR: `Photos.vue` lê `shoot[0].sessions[0].photos` direto.
 */
export function respostaFotosValida(raw: unknown): boolean {
  if (!respostaComShoot(raw)) return false;
  const sessions = (raw.shoot[0] as { sessions?: unknown }).sessions;
  return (
    Array.isArray(sessions) &&
    ehObjeto(sessions[0]) &&
    Array.isArray(sessions[0].photos)
  );
}

function secoesPorParte(raw: unknown): unknown[][] | null {
  if (!respostaComShoot(raw)) return null;
  const partes = raw.shoot.map((p) => (p as { sessions?: unknown }).sessions);
  return partes.every(Array.isArray) ? (partes as unknown[][]) : null;
}

function mesmaLista(a: unknown, b: unknown): boolean {
  return (
    Array.isArray(a) &&
    Array.isArray(b) &&
    a.length === b.length &&
    a.every((v, i) => v === b[i])
  );
}

/**
 * A resposta do cliente pode substituir a que está na tela sem remontar o
 * `Photos` (o carrossel fica onde o usuário está): mesmas partes e seções, e a
 * 1ª seção igual, que é o que o `Photos` copia no setup (fotos, nome, ids).
 */
export function trocaSemRemontar(atual: unknown, nova: unknown): boolean {
  const a = secoesPorParte(atual);
  const b = secoesPorParte(nova);
  if (!a || !b || a.length !== b.length) return false;
  if (a.some((secoes, i) => secoes.length !== b[i].length)) return false;

  const primeiraA = a[0][0] as { name?: unknown; photos?: unknown; photosId?: unknown };
  const primeiraB = b[0][0] as { name?: unknown; photos?: unknown; photosId?: unknown };
  if (!ehObjeto(primeiraA) || !ehObjeto(primeiraB)) return false;
  return (
    primeiraA.name === primeiraB.name &&
    mesmaLista(primeiraA.photos, primeiraB.photos) &&
    mesmaLista(primeiraA.photosId, primeiraB.photosId)
  );
}

/**
 * A resposta completa de `v3/modelos/{url}/videos` serve para tocar: toda
 * parte tem a lista `videos` (os componentes leem `shoot[i].videos` direto).
 */
export function respostaVideosValida(raw: unknown): boolean {
  if (!respostaComShoot(raw)) return false;
  return raw.shoot.every((p) => Array.isArray((p as { videos?: unknown }).videos));
}

const STORAGE_ADMIN = "https://admin.belladasemana.com.br/storage/";
const STORAGE_DOMINIO_PRINCIPAL = "https://belladasemana.com.br/storage/";
// Pastas de miniatura que o domínio principal serve: pôster do /video e
// miniatura do making-of (conferido em 06/10, com cache da Cloudflare).
const PASTAS_DE_MINIATURA = ["videos/", "thumb-previews/"];

/**
 * Pôster das partes do /video e do /making-of pelo domínio principal. O admin.
 * está fora da Cloudflare, sem cache e com robots `Disallow: /`; o mesmo
 * arquivo sai do domínio principal pela Cloudflare, com cache (em
 * /storage/videos o nginx de lá só libera imagem). Decisão do Eduardo (06/10)
 * para o pôster do /video: trocar só no front.
 */
export function posterNoDominioPrincipal(url: unknown): unknown {
  if (typeof url !== "string" || !url.startsWith(STORAGE_ADMIN)) return url;
  const caminho = url.slice(STORAGE_ADMIN.length);
  return PASTAS_DE_MINIATURA.some((pasta) => caminho.startsWith(pasta))
    ? STORAGE_DOMINIO_PRINCIPAL + caminho
    : url;
}

/**
 * A resposta de vídeos (ou de making-of, que tem a mesma forma
 * `shoot[].partThumb`) com o pôster de cada parte no domínio principal.
 */
export function videosComPosterNoDominioPrincipal(raw: unknown): unknown {
  if (!respostaComShoot(raw)) return raw;
  return {
    ...raw,
    shoot: raw.shoot.map((parte) => ({
      ...(parte as Record<string, unknown>),
      partThumb: posterNoDominioPrincipal((parte as { partThumb?: unknown }).partThumb),
    })),
  };
}

/** Quem assina recebe os vídeos de verdade; o anônimo recebe só a "preview". */
export function ehVisaoAssinante(raw: unknown): boolean {
  if (!respostaVideosValida(raw)) return false;
  const videos = ((raw as { shoot: { videos: unknown[] }[] }).shoot[0]).videos;
  const primeiro = videos[0] as { name?: unknown } | undefined;
  return !!primeiro && primeiro.name !== "preview";
}

/**
 * A resposta completa de `v3/modelos/{url}/makingOf` serve para tocar: toda
 * parte tem a lista `lives` (os componentes leem `shoot[i].lives` direto).
 */
export function respostaMakingOfValida(raw: unknown): boolean {
  if (!respostaComShoot(raw)) return false;
  return raw.shoot.every((p) => Array.isArray((p as { lives?: unknown }).lives));
}

/**
 * Making-of de assinante: a 1ª parte tem making-of e ele não é a "preview".
 * Quem assina sem acesso ao making-of também recebe a prévia.
 */
export function ehVisaoAssinanteMakingOf(raw: unknown): boolean {
  if (!respostaMakingOfValida(raw)) return false;
  const lives = ((raw as { shoot: { lives: unknown[] }[] }).shoot[0]).lives;
  const primeiro = lives[0] as { name?: unknown } | undefined;
  return !!primeiro && primeiro.name !== "preview";
}

/**
 * A resposta de `v2/modelos/{url}/lives` serve para a página: tem a lista
 * `liveVideos` (os v2 devolvem 200 `{message}` nos erros).
 */
export function respostaLivesValida(raw: unknown): boolean {
  return ehObjeto(raw) && !("message" in raw) && Array.isArray(raw.liveVideos);
}

/**
 * Lives de assinante: a 1ª não é a "preview". Lista vazia conta como de
 * assinante, como antes do SSR: o `Lives` mostra a aba sem player e sem o
 * convite de prévia.
 */
export function ehVisaoAssinanteLive(raw: unknown): boolean {
  if (!respostaLivesValida(raw)) return false;
  const primeira = (raw as { liveVideos: unknown[] }).liveVideos[0] as
    | { name?: unknown }
    | undefined;
  return primeira?.name !== "preview";
}
